import { lazy, Suspense } from "react";
import LandingHero from "../../components/public/LandingHero";
import { useI18n } from "@/lib/i18n";
import usePageMeta from "@/hooks/usePageMeta";
import usePlatformSetting from "@/hooks/usePlatformSettings";

// Below-fold sections lazy-loaded to improve TTI
const LandingHowItWorks = lazy(() => import("../../components/public/LandingHowItWorks"));
const LandingFeatures = lazy(() => import("../../components/public/LandingFeatures"));
const LandingRevenue = lazy(() => import("../../components/public/LandingRevenue"));
const LandingAllamSupport = lazy(() => import("../../components/public/LandingAllamSupport"));
const LandingTestimonials = lazy(() => import("../../components/public/LandingTestimonials"));
const LandingPricing = lazy(() => import("../../components/public/LandingPricing"));
const LandingContact = lazy(() => import("../../components/public/LandingContact"));
const LandingCTA = lazy(() => import("../../components/public/LandingCTA"));

const FAQ_AR = [
  { q: "هل التجربة المجانية تحتاج بطاقة ائتمان؟", a: "لا، التجربة المجانية 14 يوم بدون أي بيانات دفع. تقدر تجرّب كل المميزات بحرية." },
  { q: "هل أقدر أغيّر خطتي لاحقاً؟", a: "نعم، تقدر تترقّى أو تنزّل خطتك في أي وقت من إعدادات الحساب." },
  { q: "كيف أستقبل مدفوعات من الطلاب؟", a: "عندك خياران مرنان: بوابة Stripe لأصحاب السجل التجاري أو وثيقة العمل الحر، أو التحويل البنكي المنظّم داخل المنصة." },
  { q: "هل في عمولة على المبيعات؟", a: "لا نأخذ أي عمولة على مبيعاتك، تحصل على المبلغ كاملاً (بعد رسوم بوابة الدفع فقط)." },
  { q: "هل أقدر أستخدم نطاقي الخاص؟", a: "نعم، الخطة الاحترافية تدعم ربط نطاقك الخاص بالكامل." },
];
const FAQ_EN = [
  { q: "Does the free trial require a credit card?", a: "No. The 14-day free trial requires no payment information." },
  { q: "Can I change my plan later?", a: "Yes. You can upgrade or downgrade anytime from your account settings." },
  { q: "How do I receive payments from students?", a: "Two flexible options: Stripe, or organized bank transfers handled inside the platform." },
  { q: "Is there a commission on sales?", a: "Zero commission. You keep the full amount (minus payment gateway fees only)." },
  { q: "Can I use my own custom domain?", a: "Yes. The Pro plan fully supports connecting your own custom domain." },
];

export default function Landing() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const origin = typeof window !== "undefined" ? window.location.origin : "https://dwrrah.com";
  const faq = isAr ? FAQ_AR : FAQ_EN;

  // Platform-level branding/SEO is owned by Admin → Marketing → SEO
  // (platform_settings.platform_branding). Use it as the source of truth
  // for browser tab title, description, site_name, social image and
  // favicon so Admin edits actually reflect in the live browser tab.
  // Hardcoded strings remain as a final fallback only when branding is
  // empty (matches Worker FALLBACK_BRANDING behavior on raw HTML).
  const { value: branding } = usePlatformSetting("platform_branding", {});
  const b = branding || {};
  // Fallbacks MUST mirror Worker FALLBACK_BRANDING + Admin EFFECTIVE_DEFAULTS
  // so browser tab matches what Admin shows as the default and what bots
  // receive in raw HTML. Never diverge from these without updating both
  // workers/public-meta-renderer/src/index.ts and
  // src/components/admin/PlatformBrandingCard.jsx.
  const fallbackTitle = "Dwrrah | دورة";
  const fallbackDescription = "منصة Dwrrah لإنشاء وبيع الدورات التعليمية أونلاين.";
  const seoTitle = isAr
    ? (b.browser_title || b.seo_title_ar || b.seo_title_en || fallbackTitle)
    : (b.browser_title || b.seo_title_en || b.seo_title_ar || fallbackTitle);
  const seoDescription = isAr
    ? (b.seo_description_ar || b.seo_description_en || fallbackDescription)
    : (b.seo_description_en || b.seo_description_ar || fallbackDescription);
  const seoSiteName = b.site_name || b.platform_name || "Dwrrah | دورة";
  const seoImage = b.social_image_url || undefined;
  const seoFavicon = b.favicon_url || b.logo_url || undefined;

  usePageMeta({
    title: seoTitle,
    description: seoDescription,
    image: seoImage,
    favicon: seoFavicon,
    canonicalPath: "/",
    siteName: seoSiteName,
    keywords: isAr
      ? "منصة دورات, أكاديمية تعليمية, إنشاء كورسات, تعليم أونلاين عربي, منصة تعليمية, بيع دورات, مدرب أونلاين"
      : "online academy platform, course platform, sell courses, online teaching, LMS Arabic, coaching platform",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: seoSiteName,
        applicationCategory: "EducationalApplication",
        operatingSystem: "Web",
        description: isAr
          ? "منصة سحابية لبناء أكاديميات تعليمية: دورات، جلسات، منتجات، مدفوعات، شهادات."
          : "Cloud platform to build educational academies: courses, sessions, products, payments, certificates.",
        url: `${origin}/`,
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD", description: isAr ? "تجربة مجانية 14 يوم" : "14-day free trial" },
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "120" },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: isAr ? "الرئيسية" : "Home", item: `${origin}/` },
        ],
      },
    ],
  });

  return (
    <>
      <LandingHero />
      {/* Per-section Suspense fallbacks reserve a stable min-height for each
          below-fold lazy section. This prevents a single 100vh placeholder
          from collapsing all at once (which produced visible CLS on mobile
          when sections streamed in one by one). Each fallback is aria-hidden
          and token-free — purely a layout shim. */}
      <Suspense fallback={<div style={{ minHeight: 600 }} aria-hidden="true" />}>
        <LandingHowItWorks />
      </Suspense>
      <Suspense fallback={<div style={{ minHeight: 600 }} aria-hidden="true" />}>
        <LandingFeatures />
      </Suspense>
      <Suspense fallback={<div style={{ minHeight: 500 }} aria-hidden="true" />}>
        <LandingAllamSupport />
      </Suspense>
      <Suspense fallback={<div style={{ minHeight: 500 }} aria-hidden="true" />}>
        <LandingRevenue />
      </Suspense>
      <Suspense fallback={<div style={{ minHeight: 500 }} aria-hidden="true" />}>
        <LandingTestimonials />
      </Suspense>
      <Suspense fallback={<div style={{ minHeight: 700 }} aria-hidden="true" />}>
        <LandingPricing />
      </Suspense>
      <Suspense fallback={<div style={{ minHeight: 400 }} aria-hidden="true" />}>
        <LandingContact />
      </Suspense>
      <Suspense fallback={<div style={{ minHeight: 300 }} aria-hidden="true" />}>
        <LandingCTA />
      </Suspense>
    </>
  );
}
